import { useAgenciaStore } from "../zustand/useAgenciaStore";
import { useThemeStore } from "../zustand/useThemeStore";
import { useSystemTheme } from "../hooks/useSystemTheme";
import type { Estado } from "../interfaces/Agency";

/**
 * Obtiene los estados desde el store de agencia
 */
export function useEstados(): Estado[] {
  const { agencia } = useAgenciaStore();
  return agencia?.estados || [];
}

/**
 * Obtiene un estado por su ID
 */
export function getEstadoById(estados: Estado[], id: string): Estado | undefined {
  return estados.find((estado) => estado.id === id);
}

/**
 * Obtiene los colores de un estado según el tema actual
 * Usa colores aereo por defecto
 */
export function getEstadoColors(estado: Estado, theme: "light" | "dark"): [string, string] {
  const colores = estado.colores[theme];
  const aereo = colores.aereo;
  return [aereo.a, aereo.b];
}

/**
 * Hook para obtener los colores de un estado según el tema actual
 */
export function useEstadoColors(estado: Estado | undefined): [string, string] | null {
  const { mode } = useThemeStore();
  const systemTheme = useSystemTheme();
  const effectiveMode = mode === "system" ? systemTheme : mode;

  if (!estado) return null;

  return getEstadoColors(estado, effectiveMode);
}

/**
 * Obtiene el ID del primer estado (estado inicial por defecto)
 */
export function getEstadoInicialId(estados: Estado[]): string | null {
  return estados.length > 0 ? estados[0].id : null;
}

/**
 * Valida si un ID de estado es válido
 */
export function isValidEstadoId(estados: Estado[], id: string): boolean {
  return estados.some((estado) => estado.id === id);
}

/**
 * Obtiene el nombre formateado de un estado (reemplaza _ por espacios)
 */
export function formatEstadoNombre(id: string): string {
  return id.replaceAll("_", " ");
}
